import { alertErrorResponse } from './alertResponseHttpUtil';

/**
 * Maneja errores de servicios de manera centralizada y controla el flujo de la UI.
 * * Heurística de retorno:
 * - Si es 401: Bloquea la ejecución (Promesa pendiente) para evitar que el componente
 * continúe procesando lógica tras la expulsión.
 * - Si es GET: Retorna [] por defecto para evitar errores de iteración en tablas/listas.
 * - Otros métodos: Retorna null por defecto para validaciones condicionales en formularios.
 *
 * @param {Error} error - Objeto de error capturado (AxiosError).
 * @param {*} [valorPorDefecto] - Valor de retorno manual. Si se omite, aplica la heurística.
 * @returns {Promise<*|Pending>} Retorna el valor manejado o una promesa que nunca resuelve en 401.
 */
export const manejarErrorServicio = async (error, valorPorDefecto) => {
  const statusCode = error.response?.data?.statusCode || error.response?.status;

  if (statusCode === 401) {
    await alertErrorResponse(error.response.data);
    return new Promise(() => {});
  }

  if (error.response?.data) {
    await alertErrorResponse(error.response.data);
  }

  if (valorPorDefecto !== undefined) {
    return valorPorDefecto;
  }

  const metodo = error.config?.method?.toUpperCase();
  return metodo === 'GET' ? [] : null;
};

/**
 * Normaliza la estructura de errores provenientes de peticiones HTTP.
 * @param {Object} error - Objeto de error de Axios.
 * @returns {Promise} - Promesa rechazada con el error formateado.
 */
export const normalizarError = error => {
  const errorInstancia =
    error instanceof Error ? error : new Error(error?.message || String(error));

  if (!errorInstancia.response) {
    errorInstancia.response = {
      data: { statusCode: 0, message: 'Servidor no disponible' },
    };
    return Promise.reject(errorInstancia);
  }

  const datosOriginales =
    typeof errorInstancia.response.data === 'object'
      ? errorInstancia.response.data
      : {};

  errorInstancia.response.data = {
    statusCode: errorInstancia.response.status,
    message: datosOriginales.message || 'Error inesperado',
    ...datosOriginales,
  };

  return Promise.reject(errorInstancia);
};
